export function safeText(value = "") {
  return String(value ?? "").trim();
}

export function normalizeLower(value = "") {
  return safeText(value).toLowerCase();
}

export function humanize(value = "", fallback = "Pending") {
  const text = safeText(value);

  if (!text) return fallback;

  return text
    .replace(/[-_]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export const STATUS_LABELS = Object.freeze({
  draft: "Draft",
  pending: "Pending",
  pending_payment: "Pending payment",
  payment_action_required: "Payment action required",
  payment_processing: "Payment processing",
  paid: "Paid",
  confirmed: "Confirmed",
  processing: "Processing",
  fulfilled: "Fulfilled",
  delivered: "Delivered",
  collected: "Collected",
  cancelled: "Cancelled",
  canceled: "Cancelled",
  refunded: "Refunded",
  partially_refunded: "Partially refunded",
  failed: "Failed",
  expired: "Expired",

  not_started: "Unpaid",
  requires_payment_method: "Payment method required",
  requires_action: "Action required",
  requires_capture: "Awaiting capture",
  authorized: "Authorised",
  refund_pending: "Refund pending",

  awaiting_payment: "Awaiting payment",
  pending_fulfillment: "Processing",
  pending_pickup: "Pending collection",
  preparing: "Preparing",
  ready_to_ship: "Ready to ship",
  ready_for_pickup: "Ready for collection",
  partially_dispatched: "Partially shipped",
  dispatched: "Shipped",
  shipped: "Shipped",
  partially_delivered: "Partially delivered",
  picked_up: "Collected",
  returned: "Returned",
});

export function getStatusLabel(value = "", fallback = "Pending") {
  const status = normalizeLower(value);

  if (!status) return fallback;

  return STATUS_LABELS[status] || humanize(status, fallback);
}

export function getOrderStatusLabel(orderOrStatus = {}) {
  const value =
    typeof orderOrStatus === "object" && orderOrStatus !== null
      ? orderOrStatus.status
      : orderOrStatus;

  if (normalizeLower(value) === "paid") return "Order confirmed";
  if (normalizeLower(value) === "fulfilled" && orderOrStatus?.fulfillment?.method === "pickup") return "Collected";
  return getStatusLabel(value, "Pending");
}

export function getPaymentStatusLabel(orderOrStatus = {}) {
  const value =
    typeof orderOrStatus === "object" && orderOrStatus !== null
      ? orderOrStatus.payment?.status
      : orderOrStatus;

  return getStatusLabel(value, "Unpaid");
}

export function getFulfillmentStatusLabel(orderOrStatus = {}) {
  const value =
    typeof orderOrStatus === "object" && orderOrStatus !== null
      ? orderOrStatus.fulfillment?.status
      : orderOrStatus;

  return getStatusLabel(value, "Processing");
}

export function formatMoney(value = 0, currency = "GBP") {
  const amount = Number(value || 0);

  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: safeText(currency) || "GBP",
    }).format(Number.isFinite(amount) ? amount : 0);
  } catch {
    return `£${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
  }
}

export function formatDate(value = "", options = {}) {
  if (!value) return "Not set";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Not set";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      dateStyle: options.dateStyle || "medium",
      timeStyle: options.timeStyle || "short",
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

export function getOrderNumber(order = {}) {
  const source = order || {};

  return safeText(source.orderNumber || source.id || source._id);
}

export function getOrderItems(order = {}) {
  const source = order || {};

  return Array.isArray(source.items) ? source.items : [];
}

export function getOrderCurrency(order = {}) {
  const source = order || {};

  return safeText(source.currency || source.payment?.currency || "GBP");
}

export function getCustomerName(order = {}) {
  const source = order || {};

  return safeText(
    source.customer?.name ||
      [source.customer?.firstName, source.customer?.lastName]
        .map(safeText)
        .filter(Boolean)
        .join(" "),
  );
}

export function getPaymentLabel(order = {}) {
  const source = order || {};
  const provider = normalizeLower(source.payment?.provider);

  if (provider === "stripe") return "Card";
  if (provider === "paypal") return "PayPal";
  if (provider === "sumup") return "SumUp";

  return humanize(source.payment?.method || provider, "Payment");
}

export function getPrimaryAddress(order = {}) {
  const source = order || {};

  return (
    source.fulfillment?.shippingAddress ||
    source.shippingAddress ||
    source.billingAddress ||
    {}
  );
}

export function getStatusTone(status = "") {
  const value = normalizeLower(status);

  if (
    [
      "paid",
      "confirmed",
      "fulfilled",
      "delivered",
      "collected",
      "completed",
      "picked_up",
      "dispatched",
      "shipped",
    ].includes(value)
  ) {
    return "success";
  }

  if (
    [
      "processing",
      "payment_processing",
      "shipped",
      "dispatched",
      "preparing",
      "pending_fulfillment",
      "pending_pickup",
      "ready_for_pickup",
      "ready_to_ship",
      "requires_action",
      "requires_capture",
      "authorized",
    ].includes(value)
  ) {
    return "info";
  }

  if (
    [
      "pending",
      "pending_payment",
      "awaiting_payment",
      "not_started",
      "requires_payment_method",
    ].includes(value)
  ) {
    return "warning";
  }

  if (["cancelled", "canceled", "refunded", "partially_refunded"].includes(value)) {
    return "warning";
  }

  if (["failed", "expired", "declined", "denied"].includes(value)) {
    return "danger";
  }

  return "neutral";
}

export function getOrderStatusTone(orderOrStatus = {}) {
  const value =
    typeof orderOrStatus === "object" && orderOrStatus !== null
      ? orderOrStatus.status
      : orderOrStatus;

  return getStatusTone(value || "pending_payment");
}

export function getPaymentStatusTone(orderOrStatus = {}) {
  const value =
    typeof orderOrStatus === "object" && orderOrStatus !== null
      ? orderOrStatus.payment?.status
      : orderOrStatus;

  return getStatusTone(value || "not_started");
}

export function getFulfillmentStatusTone(orderOrStatus = {}) {
  const value =
    typeof orderOrStatus === "object" && orderOrStatus !== null
      ? orderOrStatus.fulfillment?.status
      : orderOrStatus;

  return getStatusTone(value || "pending_fulfillment");
}

export function getLineTitle(item = {}) {
  return safeText(item.productName || item.name || "Item");
}

export function getLineMeta(item = {}) {
  return [
    item.variantName,
    item.variantSku || item.sku,
    item.variantType,
    item.color,
    item.size,
    item.length,
  ]
    .map(safeText)
    .filter(Boolean)
    .join(" · ");
}

export function getLineQuantity(item = {}) {
  const qty = Number(item.qty || 0);

  return Number.isFinite(qty) && qty > 0 ? qty : 0;
}

export function getOrderTimeline(order = {}) {
  return [
    ["Placed", order.placedAt || order.createdAt],
    ["Paid", order.paidAt || order.payment?.paidAt],
    ["Fulfilled", order.fulfilledAt],
    ["Updated", order.updatedAt],
  ].filter(([, value]) => Boolean(value));
}

export function getReceiptAmounts(order = {}) {
  const amount = (value) => Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0;
  const captured = amount(order.payment?.amountCaptured);
  const refunded = amount(order.payment?.amountRefunded);
  return { captured, refunded, netPaid: Math.max(0, captured - refunded), discount: amount(order.discountTotal) };
}

export function getShipment(order = {}) {
  return order.fulfillment?.delivery || order.fulfillment || {};
}

export function getTrackingUrl(order = {}) {
  try {
    const url = new URL(getShipment(order).trackingUrl);
    return url.protocol === "https:" && !url.username && !url.password ? url.href : "";
  } catch { return ""; }
}
